import {
  AnimatedSprite,
  Application,
  Assets,
  Container,
  Rectangle,
  Texture,
  Cache,
} from "pixi.js";
import { SkillCastTile, SkillCastTileConfig } from "./skill-cast-tile";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";
import { roundedPositionOf } from "../utils/rounded-position-of";
import { ClientPokemon } from "./client-pokemon";
import { SkillStatusEffect } from "types/battle-engine/battle";
import { calculateDamage } from "../utils/calculate-damage";
import { getHealingMultiplier } from "game/duel-engine/utils/stat-scaling";

export interface ClientSkillCastTileConfig extends SkillCastTileConfig {
  app: Application;
  spriteUrl: string | undefined;
  parent?: Container;
  enemies: ClientPokemon[];
  allies: ClientPokemon[];
  statusEffects?: SkillStatusEffect[];
  sourceId?: number;
  animSpeed: number;
  animLoops: number;
  attackerEffectiveAP: number;
}

const DEFAULT_FPS = 24;

/**
 * Projectile class handles visual projectile rendering
 * - Non-directional sprite that rotates toward target
 * - Linear interpolation from start to end position
 * - Auto-destroys on completion
 * - Supports animated sprite strips (loops while flying)
 * */
export class ClientSkillCastTile extends SkillCastTile {
  private sprite: AnimatedSprite | null = null;
  private container: Container;
  private parent: Container;
  protected override enemies: ClientPokemon[];
  protected override allies: ClientPokemon[];
  private statusEffects: SkillStatusEffect[];
  private sourceId: number;
  private animSpeed: number;
  private animLoops: number;
  private currentLoop: number = 0;
  private attackerEffectiveAP: number;

  constructor(config: ClientSkillCastTileConfig) {
    super(config);

    this.parent = config.parent ?? config.app.stage;

    // Create container
    this.container = new Container();
    this.container.x = config.position.x;
    this.container.y = config.position.y;
    this.container.zIndex = config.position.y;

    this.enemies = config.enemies;
    this.allies = config.allies;
    this.statusEffects = config.statusEffects ?? [];
    this.sourceId = config.sourceId ?? 0;
    this.type = config.type;
    this.animSpeed = config.animSpeed ?? 1;
    this.animLoops = config.animLoops ?? 1;
    this.attackerEffectiveAP = config.attackerEffectiveAP;

    // Add to parent container
    this.parent.addChild(this.container);
  }

  public override fire(
    currentTick: number,
    resultHP?: number,
    targetId?: number,
  ) {
    if (this.animSpeed < 0 && this.sprite) {
      this.sprite.gotoAndPlay(this.sprite.totalFrames - 1);
    } else {
      this.sprite?.play();
    }

    // Check if this is healing (negative damage)
    const isHealing = this.healing > 0;

    let target: ClientPokemon | undefined;

    if (isHealing) {
      // Healing: find target in allies
      if (targetId) {
        target = this.allies.find((a) => a.id === targetId);
      } else {
        target = this.allies.find((a) => {
          const targetPos = roundedPositionOf(a.Position);
          const tilePos = roundedPositionOf(this.position);
          return targetPos.x === tilePos.x && targetPos.y === tilePos.y;
        });
      }

      if (!target) return;

      const healMultiplier = getHealingMultiplier(this.attackerEffectiveAP);
      const healAmount = Math.round(Math.abs(this.healing) * healMultiplier);

      // Show visual healing effect
      target.showFloatingHeal(healAmount);

      // Apply status effects after healing (e.g., buffs)
      if (this.statusEffects.length > 0) {
        for (const statusEffect of this.statusEffects) {
          target.applyStatusEffect(statusEffect, this.sourceId, currentTick);
        }
        target.updateStatusEffectIcons();
      }
    } else {
      // Damage: find target in enemies
      if (targetId) {
        target = this.enemies.find((e) => e.id === targetId);
      } else {
        target = this.enemies.find((e) => {
          const targetPos = roundedPositionOf(e.Position);
          const tilePos = roundedPositionOf(this.position);
          return targetPos.x === tilePos.x && targetPos.y === tilePos.y;
        });
      }

      if (!target) return;

      const damage =
        this.damage > 0
          ? calculateDamage({
              inbound: {
                damage: this.damage,
                type: this.type,
                attackerStatusEffects: this.statusEffects,
                attackerEffectiveAP: this.attackerEffectiveAP,
              },
              defender: {
                pokemon: target.PokemonData,
                defenderStatusEffects: Array.from(
                  target.ActiveStatusEffects.values(),
                ),
                defenderDodgeChance: target.dodgeChance,
              },
            })
          : 0;

      if (damage) {
        target.takeDamage(damage);
      }

      // Apply status effects after damage
      if (this.statusEffects.length > 0) {
        for (const statusEffect of this.statusEffects) {
          target.applyStatusEffect(statusEffect, this.sourceId, currentTick);
        }
        target.updateStatusEffectIcons();
      }
    }
  }

  /**
   * Load projectile sprite and set up initial rotation
   */
  public async initSprite(
    spriteUrl: string,
    spriteScale?: number,
    spriteScaleX?: number,
    spriteScaleY?: number,
  ) {
    try {
      // Resolve the URL properly for Vite

      const frames = await this.createFrames(spriteUrl);

      this.sprite = new AnimatedSprite(frames);
      this.sprite.anchor.set(0.5);

      // Apply scale - individual X/Y takes precedence over uniform scale
      const scaleX = spriteScaleX ?? spriteScale ?? 1;
      const scaleY = spriteScaleY ?? spriteScale ?? 1;
      this.sprite.scale.set(scaleX, scaleY);

      this.sprite.loop = this.animLoops > 1;
      this.sprite.animationSpeed = (DEFAULT_FPS / 60) * (this.animSpeed ?? 1); // Convert FPS to PixiJS speed

      this.sprite.onComplete = () => {
        this.destroy();
      };

      this.sprite.onLoop = () => {
        this.currentLoop++;
        if (this.currentLoop >= this.animLoops) {
          this.destroy();
        }
      };

      // Calculate initial rotation
      this.container.addChild(this.sprite);
    } catch (error) {
      console.error("Failed to load projectile sprite:", spriteUrl, error);
      this.destroy();
    }
  }

  /**
   * Create animation frames from sprite strip
   * If frameCount not specified, treats as single frame
   */
  private async createFrames(spriteUrl: string): Promise<Texture[]> {
    if (!spriteUrl) {
      throw new Error("Sprite URL is required");
    }

    const texture = await Assets.load<Texture>(spriteUrl);

    const frameCount = VFX_SPRITE_FRAME_COUNT_DATA[spriteUrl];

    if (!frameCount)
      throw new Error(`Frame count not found for sprite: ${spriteUrl}`);

    const frames: Texture[] = [];
    const frameWidth = texture.width / frameCount;
    const frameHeight = texture.height;

    for (let i = 0; i < frameCount; i++) {
      const cacheKey = spriteUrl + "__frame-" + i;

      if (Cache.has(cacheKey)) {
        frames.push(Cache.get(cacheKey));
      } else {
        const frame = new Texture({
          source: texture.source,
          frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
        });
        Cache.set(cacheKey, frame);
        frames.push(frame);
      }
    }

    return frames;
  }

  /**
   * Clean up resources and remove from stage
   */
  public destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.container.destroy({ children: true });
  }
}
